import React from 'react'
import { theme } from 'antd'
import { flexRender } from '@tanstack/react-table'
import type { RowData, Table } from '@tanstack/react-table'
import { fontSize, fontWeight, lineHeight, spacing } from '@vacationtracker/shared/constants/ThemeSetting'
import type { DataGridFeatures } from './features'
import { DataGridStyles } from './DataGridStyles'

/** Exported because callers size their own paging against it. */
export const DATA_GRID_ROW_HEIGHT = 56

/** The `compact` density's row. Twelve pixels off every row is three more people in view on a
 * 900px-tall window, which is where most of this grid is read. */
export const DATA_GRID_COMPACT_ROW_HEIGHT = 44

/** The header's one line of text. Both header heights are built around it, so neither can drift
 * from the type it has to hold. */
const HEADER_LINE_BOX = Math.round(fontSize.sm * lineHeight.base)

/** Derived from the same tokens `headerCell` is built out of - 16px of padding top and bottom, one
 * 12px line, and the 1px rule under it - rather than measured, so it cannot drift from the thing it
 * describes. Covers the column-header row only; a `columnGroups` band adds another. */
export const DATA_GRID_HEADER_HEIGHT = spacing.base * 2 + HEADER_LINE_BOX + 1

/** The `compact` density's header. */
export const DATA_GRID_COMPACT_HEADER_HEIGHT = 40

/** The header's height is the fixed quantity - a band pins the row below it at exactly this offset -
 * so the padding is what gives, not the other way round. */
const headerPaddingY = (headerHeight: number): number => (headerHeight - HEADER_LINE_BOX - 1) / 2

/** Fallback width for a column absent from `columnWidths`. */
const FALLBACK_COLUMN_WIDTH = 150

/** The share of the container the frozen groups may take before they start coming off. */
const PIN_BUDGET = 0.5

/** How far a flex column may grow past its measured width when filling leftover container space.
 * Past the cap the leftover goes to the trailing spacer instead - with most columns hidden, an
 * uncapped flex column would balloon to the whole table's width. */
const FLEX_GROWTH_CAP = 2

/** Enough rows to fill a typical viewport without over-committing to a guessed page size. */
const SKELETON_ROW_COUNT = 8

type RowIndexVar = React.CSSProperties & { '--vt-grid-row-index': number }

/** Feeds both staggers - the skeleton's sweep and the reveal's fade. One custom property on the row
 * reaches every bar inside it. Trailing skeletons continue the count, so the sweep runs on through
 * the join with the real rows rather than restarting at the boundary. */
const rowIndexVar = (rowIndex: number): RowIndexVar => ({ '--vt-grid-row-index': rowIndex })
const SKELETON_BAR_WIDTHS = ['88%', '62%', '74%', '50%', '80%']

/** Bar widths vary by position so a row of skeletons doesn't read as one repeated tile. */
function SkeletonBar({ wide, seed }: { wide: boolean; seed: number }) {
  const width = wide ? '70%' : SKELETON_BAR_WIDTHS[seed % SKELETON_BAR_WIDTHS.length]
  return <div className="vt-grid-skeleton-bar" style={{ height: 12, width, maxWidth: wide ? 160 : 90 }} />
}

export interface DataGridColumnGroup {
  id: string
  label: React.ReactNode
  /** A contiguous run of column ids in the current visible order. */
  columnIds: string[]
}

export interface DataGridProps<TData extends RowData> {
  /** The caller owns the table instance, so controlled column visibility and row selection stay
   * where the rest of the page's state lives. */
  table: Table<DataGridFeatures, TData>
  loading: boolean
  /** Fixed px width per column id (chrome columns included), rendered as a colgroup under
   * `table-layout: fixed` so content - an open editor included - can never move a column.
   * Required, not optional: the layout contract depends on the caller having measured. */
  columnWidths: Record<string, number>
  /** Ids of the leading columns to freeze while the rest scroll horizontally. Only the leading
   * contiguous run pins, and only its last member draws the edge shadow. Given by id, never by
   * position: freezing "column 1" pins whatever happens to sit there. */
  stickyLeftColumnIds?: string[]
  /** Trailing columns pinned to the right edge while the middle scrolls (the mirror of
   * `stickyLeftColumnIds`). Must be the table's last columns, in display order. */
  stickyRightColumnIds?: string[]
  /** Columns that share whatever width the container has beyond what every column measured to, in
   * proportion to what each already takes - so a wide screen is filled by the table rather than by
   * a dead strip at its right edge. */
  flexColumnIds?: string[]
  /** Columns holding icons rather than text - checkbox and row-action columns. They keep their
   * full content instead of truncating, and draw no skeleton bar while loading. */
  chromeColumnIds?: string[]
  /** The column whose content reveals on row hover or keyboard focus, for a row-actions menu. */
  rowActionsColumnId?: string
  /** Columns holding numbers right-align - header and cells share one right edge. */
  numericColumnIds?: string[]
  /** Columns whose value wraps onto a second line instead of being cut off. For a column sized to
   * its own content against a cap: past the cap, truncating would hide the very thing the column
   * was measured for. The row grows to fit, because its height is a floor. */
  wrapColumnIds?: string[]
  /** Skeleton rows appended AFTER the real ones, standing in for the page about to load, so
   * scrolling into them reads as arriving rather than as hitting the end. Zero renders none. */
  trailingSkeletonRows?: number
  /** How many rows the first skeleton sketches. A caller that has measured its own page should say
   * so: the point of a skeleton is that nothing moves when the rows land. */
  skeletonRows?: number
  /** Columns whose value has not arrived yet. Their cells sketch a bar while every other cell in
   * the row reads normally - a late column must never blank rows already on screen. Unlike
   * `loading`, which is the whole grid, this is per column. */
  pendingColumnIds?: string[]
  /** Rendered inside the scroll container, after the table, for anything that has to be watched
   * against the ROWS scrolling rather than the page's - an IntersectionObserver rooted at
   * `containerRef` needs its target in the same box. */
  footer?: React.ReactNode
  /** Replaces a column's header content, keyed by column id - for putting a control inside the
   * sheet's header cell rather than in a band above it. */
  headerOverrides?: Record<string, React.ReactNode>
  /** A spanning band drawn ABOVE the header row, grouping runs of columns. Each entry must name a
   * contiguous run in the current visible order; anything not covered gets an empty spanning cell,
   * so the band can never drift from the columns under it. */
  columnGroups?: DataGridColumnGroup[]
  /** Caps the grid's height so it scrolls its OWN rows, which is the only thing that makes the
   * sticky header stick: `position: sticky` needs a scrollport, and without a height the container
   * is only ever as tall as its content. Omit it and the page scrolls instead. */
  maxHeight?: number
  /** `uniform` draws every rule, the header and the column-group band included, in the row rule's
   * token (colorSplit), so the grid has one line weight. Default `standard` is the grid as it has
   * always drawn. */
  separators?: 'standard' | 'uniform'
  /** `compact` shortens the row and the header, for grids read on short windows where the count of
   * rows in view is the point. Default `comfortable` is the grid as it ships. The row height stays
   * a floor either way, so a cell that wraps to two lines grows its row rather than clipping. */
  density?: 'comfortable' | 'compact'
  /** aria-sort for a column's header cell, when the caller sorts by it. */
  ariaSortFor?: (columnId: string) => 'ascending' | 'descending' | undefined
  /** An editing model focuses cells by querying inside this element. */
  containerRef?: React.MutableRefObject<HTMLDivElement | null>
  /** Clipboard paste lands on the grid, not on any one cell - the focused cell decides the
   * target column. */
  onPaste?: (event: React.ClipboardEvent<HTMLDivElement>) => void
  /** Commits an open editor when the pointer goes down elsewhere. Capture-phase so it runs
   * before the new cell opens its own editor. */
  onPointerDownCapture?: (event: React.PointerEvent<HTMLDivElement>) => void
  /** The mouse-event counterpart of `onPointerDownCapture`, for the browsers and synthetic events
   * that deliver only the mouse sequence. */
  onMouseDownCapture?: (event: React.MouseEvent<HTMLDivElement>) => void
}

/**
 * Vacation Tracker's data grid: a fixed-layout sheet for tables that freeze columns, host per-cell
 * interaction, or page by scrolling. The caller owns the TanStack table instance, this component
 * only draws it. Reach for `DataTable` instead when a config-driven antd table with a
 * column-visibility popover and prev/next pagination is what the page needs.
 *
 * Renders no strings of its own, so it takes no i18n dependency. Zero rows draws an empty body: an
 * empty state belongs beside the grid, because it usually has to offer a way out of filter state
 * the grid has no business knowing.
 */
export function DataGrid<TData extends RowData>({
  table,
  loading,
  columnWidths,
  stickyLeftColumnIds = [],
  stickyRightColumnIds = [],
  flexColumnIds = [],
  chromeColumnIds = [],
  rowActionsColumnId,
  numericColumnIds = [],
  wrapColumnIds = [],
  trailingSkeletonRows = 0,
  skeletonRows = SKELETON_ROW_COUNT,
  pendingColumnIds = [],
  footer,
  headerOverrides,
  columnGroups,
  maxHeight,
  separators = 'standard',
  density = 'comfortable',
  ariaSortFor,
  containerRef,
  onPaste,
  onPointerDownCapture,
  onMouseDownCapture,
}: DataGridProps<TData>) {
  const { token } = theme.useToken()
  const gridRef = React.useRef<HTMLDivElement | null>(null)
  const [containerWidth, setContainerWidth] = React.useState(0)

  // The caller's ref and ours are the same node: a consumer focuses cells through its copy, the
  // fill and the pin budget below measure through this one.
  const attachContainer = React.useCallback(
    (node: HTMLDivElement | null) => {
      gridRef.current = node
      if (containerRef) containerRef.current = node
    },
    [containerRef]
  )

  // Measured synchronously first, observer second. A ResizeObserver's first callback is delivered
  // on a frame, and a page opened in a background tab renders no frames at all - the grid would
  // sit at width 0 until the tab was looked at.
  React.useLayoutEffect(() => {
    const node = gridRef.current
    if (!node) return
    // clientWidth, not the border box: this element scrolls, and the border box would overshoot by
    // the scrollbar's width, handing the fill more slack than exists.
    setContainerWidth(node.clientWidth)
    // Absent in jsdom, where there is no layout to observe anyway: width stays 0, so nothing fills
    // and every pin holds.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver((entries) => setContainerWidth(entries[0].contentRect.width))
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const visibleColumns = table.getVisibleLeafColumns()
  const hasBand = Boolean(columnGroups?.some((group) => visibleColumns.some((column) => group.columnIds.includes(column.id))))

  const compact = density === 'compact'
  const rowHeight = compact ? DATA_GRID_COMPACT_ROW_HEIGHT : DATA_GRID_ROW_HEIGHT
  const headerHeight = compact ? DATA_GRID_COMPACT_HEADER_HEIGHT : DATA_GRID_HEADER_HEIGHT

  // Demoted below the data, so the header reads as a label for the column rather than a row of
  // content. Colour stays SECONDARY, not tertiary: antd's tertiary is ~3.9:1 on white and fails AA
  // at this size. Size and padding come off the shared scale so the same header cannot be two
  // different sizes on adjacent screens.
  const headerCell: React.CSSProperties = {
    position: 'sticky',
    // With a band above it the column-header row pins one band-height down; both rows stay
    // visible instead of stacking on the same line where the band would lose.
    top: hasBand ? headerHeight : 0,
    zIndex: 2,
    textAlign: 'left',
    padding: `${headerPaddingY(headerHeight)}px ${spacing.base}px`,
    background: token.colorBgContainer,
    color: token.colorTextSecondary,
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    lineHeight: `${HEADER_LINE_BOX}px`,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    borderBottom: `1px solid ${separators === 'uniform' ? token.colorSplit : token.colorBorderSecondary}`,
    whiteSpace: 'nowrap',
  }

  // Keeps the header's bottom border: the rule separates the band from the column names, and it
  // makes the band's height exactly DATA_GRID_HEADER_HEIGHT - the offset the row below pins at.
  const groupCell: React.CSSProperties = {
    ...headerCell,
    top: 0,
    height: headerHeight,
    boxSizing: 'border-box',
    textAlign: 'left',
    color: token.colorTextSecondary,
  }

  const bodyCell: React.CSSProperties = {
    padding: `${compact ? spacing.xxs : spacing.xs}px ${spacing.base}px`,
    borderBottom: `1px solid ${token.colorSplit}`,
    color: token.colorText,
    // A minimum row height (td height is a floor, not a clip) so an open editor never grows
    // the row; every editor fits inside it.
    height: rowHeight,
    verticalAlign: 'middle',
    // The anchor for a cell-level ring overlay: `inset: 0` against this resolves to the whole
    // cell, so a ring frames the cell without knowing the padding.
    position: 'relative',
  }

  // Numeric columns keep any derived caption on its own line, and chrome columns hold icons rather
  // than text, so neither truncates.
  const columnStyles = (columnId: string): React.CSSProperties => {
    const numeric = numericColumnIds.includes(columnId)
    const isChrome = chromeColumnIds.includes(columnId)
    const wraps = wrapColumnIds.includes(columnId)
    return {
      ...(numeric ? { textAlign: 'right' } : {}),
      // `anywhere` rather than `break-word`: an email or an id is one unbroken token, so without it
      // there is no break opportunity and the value overflows its column instead of wrapping.
      ...(wraps && !isChrome ? { whiteSpace: 'normal', overflowWrap: 'anywhere' } : {}),
      ...(!wraps && !numeric && !isChrome ? { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } : {}),
    }
  }

  const measuredWidth = (columnId: string): number => columnWidths[columnId] ?? FALLBACK_COLUMN_WIDTH

  // Every column is measured to its own content, so on a container wider than the data needs the
  // leftover is spread across the flex columns in proportion to what they already take: the widest
  // gains the most and none is stretched past what its content can use. Below the measured total
  // there is no slack to share, so the table stays at its measured width and scrolls.
  const flexIds = visibleColumns.map((column) => column.id).filter((id) => flexColumnIds.includes(id))
  const measuredTotal = visibleColumns.reduce((sum, column) => sum + measuredWidth(column.id), 0)
  const flexTotal = flexIds.reduce((sum, id) => sum + measuredWidth(id), 0)
  const slack = containerWidth > 0 ? containerWidth - measuredTotal : 0
  const filling = slack > 0 && flexTotal > 0
  const widthOf = (columnId: string): number =>
    filling && flexIds.includes(columnId)
      ? Math.min(measuredWidth(columnId) + (slack * measuredWidth(columnId)) / flexTotal, measuredWidth(columnId) * FLEX_GROWTH_CAP)
      : measuredWidth(columnId)
  // Once the flex columns have taken the slack the columns fill the container exactly, so there is
  // nothing left for a spacer to absorb - unless the growth cap kicked in, in which case the
  // remainder goes back to the spacer.
  const filledWidth = visibleColumns.reduce((sum, column) => sum + widthOf(column.id), 0)
  const spacerColumn = !filling || filledWidth < containerWidth - 1

  // Both loops stop at the first unpinned column, so a caller naming a column further in can never
  // strand a pinned cell in the middle of the scrolling region.
  const candidateLeftIds: string[] = []
  for (const column of visibleColumns) {
    if (!stickyLeftColumnIds.includes(column.id)) break
    candidateLeftIds.push(column.id)
  }
  const candidateRightIds: string[] = []
  for (let i = visibleColumns.length - 1; i >= 0; i--) {
    const id = visibleColumns[i].id
    if (!stickyRightColumnIds.includes(id)) break
    candidateRightIds.unshift(id)
  }

  const spacerSplit = visibleColumns.length - candidateRightIds.length

  // A frozen column is only worth what is left to scroll beside it, so past the budget the groups
  // come off in order of what they are worth: the trailing group first, then the leading chrome
  // column, leaving the column you navigate by. Measured against the CONTAINER rather than the
  // viewport, because a sidebar opening changes the grid's space without changing the window.
  const totalOf = (ids: string[]) => ids.reduce((sum, id) => sum + widthOf(id), 0)
  const overBudget = (left: string[], right: string[]) => containerWidth > 0 && totalOf(left) + totalOf(right) > containerWidth * PIN_BUDGET
  const activeRightIds = overBudget(candidateLeftIds, candidateRightIds) ? [] : candidateRightIds
  const activeLeftIds = overBudget(candidateLeftIds, activeRightIds) ? candidateLeftIds.filter((id) => !chromeColumnIds.includes(id)) : candidateLeftIds

  const leftOffsets: Record<string, number> = {}
  let stickyLeftEdgeId: string | undefined
  let accumulatedLeft = 0
  for (const id of activeLeftIds) {
    leftOffsets[id] = accumulatedLeft
    stickyLeftEdgeId = id
    accumulatedLeft += widthOf(id)
  }

  // Background comes from the .vt-grid-sticky class rather than inline, so the row hover fill can
  // repaint sticky cells too - an inline style would beat the hover rule.
  const stickyStyles = (columnId: string): React.CSSProperties =>
    columnId in leftOffsets
      ? {
          position: 'sticky',
          left: leftOffsets[columnId],
          zIndex: 1,
          ...(columnId === stickyLeftEdgeId ? { boxShadow: `2px 0 4px ${token.colorFillSecondary}` } : {}),
        }
      : {}

  // Each member's `right` is the width of everything after it, and only the group's leftmost member
  // draws the edge shadow - an interior shadow would slice the group in two.
  const rightOffsets: Record<string, number> = {}
  let stickyRightEdgeId: string | undefined
  let accumulatedRight = 0
  for (let i = activeRightIds.length - 1; i >= 0; i--) {
    const id = activeRightIds[i]
    rightOffsets[id] = accumulatedRight
    stickyRightEdgeId = id
    accumulatedRight += widthOf(id)
  }
  const stickyRightStyles = (columnId: string): React.CSSProperties =>
    columnId in rightOffsets
      ? {
          position: 'sticky',
          right: rightOffsets[columnId],
          zIndex: 1,
          ...(columnId === stickyRightEdgeId ? { boxShadow: `-2px 0 4px ${token.colorFillSecondary}` } : {}),
        }
      : {}
  const cellClasses = (columnId: string): string | undefined => {
    const classes = [
      columnId === rowActionsColumnId ? 'vt-grid-row-actions' : undefined,
      columnId in leftOffsets || columnId in rightOffsets ? 'vt-grid-sticky' : undefined,
    ].filter(Boolean)
    return classes.length ? classes.join(' ') : undefined
  }

  // The widest skeleton bar marks the identity column, wherever the caller put it - the first
  // column carrying text rather than icons.
  const primaryColumnId = visibleColumns.find((column) => !chromeColumnIds.includes(column.id))?.id

  const skeletonRow = (key: string, rowIndex: number) => {
    const renderSkeletonCell = (column: (typeof visibleColumns)[number], index: number) => (
      <td key={column.id} className={cellClasses(column.id)} style={{ ...bodyCell, ...stickyStyles(column.id), ...stickyRightStyles(column.id) }}>
        {!chromeColumnIds.includes(column.id) && <SkeletonBar wide={column.id === primaryColumnId} seed={rowIndex + index} />}
      </td>
    )
    return (
      <tr key={key} aria-hidden="true" style={rowIndexVar(rowIndex)}>
        {visibleColumns.slice(0, spacerSplit).map(renderSkeletonCell)}
        {spacerColumn && <td aria-hidden style={bodyCell} />}
        {visibleColumns.slice(spacerSplit).map((column, index) => renderSkeletonCell(column, spacerSplit + index))}
      </tr>
    )
  }

  const rows = table.getRowModel().rows

  return (
    <div
      ref={attachContainer}
      className="vt-grid"
      onPaste={onPaste}
      onPointerDownCapture={onPointerDownCapture}
      onMouseDownCapture={onMouseDownCapture}
      /* minWidth: 0 is what makes the horizontal overflow actually clip: inside a flex column a
         flex item defaults to min-width: auto, so the container grows to fit the fixed-width table
         and nothing ever scrolls, leaving a frozen column nothing to stay put against.

         `overflow` rather than `overflowX`, paired with maxHeight: both axes have to be scrollable
         for the header to have a scrollport to pin against.

         The sheet paints its own fill rather than trusting whatever it is dropped into. A frozen
         cell scrolls over content so it MUST be opaque; if the host paints nothing, the frozen and
         scrolling halves of a row land on different pixels.

         Theme colours for the scrollbar: without them Chrome draws its light default, a white track
         across the dark theme. Safari's overlay scrollbars ignore these and stay as they are. */
      style={{
        overflow: 'auto',
        minWidth: 0,
        maxWidth: '100%',
        maxHeight,
        background: token.colorBgContainer,
        scrollbarWidth: 'thin',
        scrollbarColor: `${token.colorTextQuaternary} transparent`,
      }}
    >
      {/* The grid's own rules, rendered by the grid: a caller who had to remember a separate
          stylesheet component and forgot it got a grid with no hover fill, no skeleton shimmer and
          unreachable row actions - a silent failure. */}
      <DataGridStyles />

      {/* table-layout: fixed sizes columns from the colgroup alone and ignores content, so an
          open editor can never widen a column; minWidth makes narrow viewports scroll instead
          of squeezing the widths the colgroup promises.

          width: 100% plus a trailing SPACER column is the only arrangement that holds: at 100%
          with no spacer the browser hands the leftover width to the real columns, and sizing the
          table to the measured total instead widens every ancestor. When the table is wider than
          its container the spacer collapses and the grid scrolls. */}
      <table style={{ tableLayout: 'fixed', width: '100%', minWidth: filledWidth, borderCollapse: 'separate', borderSpacing: 0 }}>
        <colgroup>
          {visibleColumns.slice(0, spacerSplit).map((column) => (
            <col key={column.id} style={{ width: widthOf(column.id) }} />
          ))}
          {spacerColumn && <col />}
          {visibleColumns.slice(spacerSplit).map((column) => (
            <col key={column.id} style={{ width: widthOf(column.id) }} />
          ))}
        </colgroup>
        <thead>
          {hasBand && columnGroups && (
            <tr>
              {(() => {
                // Walked in visible order so the band can never drift from the columns under it:
                // a run that is not in any group still gets a cell, just an unlabelled one.
                const cells: React.ReactElement[] = []
                const isGrouped = (id: string) => columnGroups.some((entry) => entry.columnIds.includes(id))
                const pinnedLeftSpan = activeLeftIds.some(isGrouped) ? 0 : activeLeftIds.length
                const trailingSpan = candidateRightIds.length
                if (pinnedLeftSpan > 0) {
                  cells.push(
                    <th
                      key="pinned-left"
                      aria-hidden
                      colSpan={pinnedLeftSpan}
                      style={{ ...groupCell, position: 'sticky', left: 0, zIndex: 3, boxShadow: `2px 0 4px ${token.colorFillSecondary}` }}
                    />
                  )
                }
                let index = pinnedLeftSpan
                const endIndex = visibleColumns.length - trailingSpan
                while (index < endIndex) {
                  const columnId = visibleColumns[index].id
                  const group = columnGroups.find((entry) => entry.columnIds.includes(columnId))
                  if (!group) {
                    let span = 0
                    while (index + span < endIndex && !isGrouped(visibleColumns[index + span].id)) span++
                    // aria-hidden because it is spacing, not a header: an unlabelled <th> is an
                    // axe empty-table-header violation, and the columns under it are already
                    // named by the real header row below.
                    cells.push(<th key={`ungrouped-${index}`} aria-hidden colSpan={span} style={groupCell} />)
                    index += span
                    continue
                  }
                  let span = 0
                  while (index + span < endIndex && group.columnIds.includes(visibleColumns[index + span].id)) span++
                  cells.push(
                    <th key={group.id} colSpan={span} scope="colgroup" style={{ ...groupCell, textAlign: 'center' }}>
                      {group.label}
                    </th>
                  )
                  index += span
                }
                if (spacerColumn) cells.push(<th key="spacer" aria-hidden style={groupCell} />)
                if (trailingSpan > 0) {
                  cells.push(
                    <th
                      key="pinned-right"
                      aria-hidden
                      colSpan={trailingSpan}
                      style={activeRightIds.length > 0 ? { ...groupCell, position: 'sticky', right: 0, zIndex: 3, boxShadow: `-2px 0 4px ${token.colorFillSecondary}` } : groupCell}
                    />
                  )
                }
                return cells
              })()}
            </tr>
          )}
          {table.getHeaderGroups().map((headerGroup) => {
            const renderHeaderCell = (header: (typeof headerGroup.headers)[number]) => (
              <th
                key={header.id}
                className={cellClasses(header.column.id)}
                aria-sort={ariaSortFor?.(header.column.id)}
                style={{
                  ...headerCell,
                  ...columnStyles(header.column.id),
                  ...stickyStyles(header.column.id),
                  ...stickyRightStyles(header.column.id),
                  // A pinned header cell sits above BOTH axes, so it has to outrank the row
                  // header (zIndex 2) as well as the body's pinned cells.
                  ...(header.column.id in leftOffsets || header.column.id in rightOffsets ? { zIndex: 3 } : {}),
                }}
              >
                {headerOverrides?.[header.column.id] ?? (header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext()))}
              </th>
            )
            return (
              <tr key={headerGroup.id}>
                {headerGroup.headers.slice(0, spacerSplit).map(renderHeaderCell)}
                {spacerColumn && <th aria-hidden style={headerCell} />}
                {headerGroup.headers.slice(spacerSplit).map(renderHeaderCell)}
              </tr>
            )
          })}
        </thead>
        {/* Busy while a COLUMN is still filling in too, not only while the whole grid is: a reader
            waiting on one late column is waiting on this table. */}
        <tbody aria-busy={loading || pendingColumnIds.length > 0}>
          {loading && Array.from({ length: skeletonRows }, (_, rowIndex) => skeletonRow(`skeleton-${rowIndex}`, rowIndex))}

          {!loading &&
            rows.map((row, rowIndex) => {
              const cells = row.getVisibleCells()
              const renderBodyCell = (cell: (typeof cells)[number], cellIndex: number) => (
                <td
                  key={cell.id}
                  data-qa-column={cell.column.id}
                  className={cellClasses(cell.column.id)}
                  style={{ ...bodyCell, ...columnStyles(cell.column.id), ...stickyStyles(cell.column.id), ...stickyRightStyles(cell.column.id) }}
                >
                  {pendingColumnIds.includes(cell.column.id) && !chromeColumnIds.includes(cell.column.id) ? (
                    <SkeletonBar wide={false} seed={rowIndex + cellIndex} />
                  ) : (
                    flexRender(cell.column.columnDef.cell, cell.getContext())
                  )}
                </td>
              )
              return (
                <tr key={row.id} data-qa-row={row.id} className="vt-grid-row" style={rowIndexVar(rowIndex)}>
                  {cells.slice(0, spacerSplit).map(renderBodyCell)}
                  {spacerColumn && <td aria-hidden style={bodyCell} />}
                  {cells.slice(spacerSplit).map((cell, index) => renderBodyCell(cell, spacerSplit + index))}
                </tr>
              )
            })}

          {/* Appended, not substituted: the real rows stay put while the incoming page is
              sketched under them, so nothing already on screen moves as it fills in. */}
          {!loading && Array.from({ length: trailingSkeletonRows }, (_, rowIndex) => skeletonRow(`trailing-skeleton-${rowIndex}`, rows.length + rowIndex))}
        </tbody>
      </table>
      {footer}
    </div>
  )
}

export default DataGrid
